import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Formik } from 'formik';

import { UTILITY_TYPE_ID_INTERNET } from 'common/utility/constants';

import { createFormValidationFn } from 'client/helpers/formikHelpers';
import { SR_API_ACTION_UTILITY_PROVIDER_GET_BY_AUTOCOMPLETE } from 'api/utilityActionTypes';

import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';

import SrBox from 'client/components/SrBox';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import SrText from 'client/components/SrText';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_THEME_ADOBE_GREY,
} from 'client/components/SrButton/SrButton';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';

const getFormFields = ({ providerMatches, onAutocompleteProvider }) => {
  return [
    {
      id: 'providerName',
      title: 'Search internet company',
      type: 'autocomplete',
      properties: {
        placeholder: 'type company here...',
        choices: (providerMatches || []).map(({ title }) => title),
        onSuggestionsFetchRequested: onAutocompleteProvider,
        onChange: ({ newValue, formikBag }) => {
          const providerMatch = (providerMatches || []).find(({ title }) => newValue === title);
          formikBag.setFieldValue('providerId', providerMatch ? providerMatch.id : null);
        },
        filterFunc: () => {
          return true;
        },
      },
      validations: ['required'],
    },
    {
      id: 'providerId',
      type: 'hidden',
    },
  ];
};

const SrUtilitySetupTransferInternetFlowCurrentProviderNameModal = ({ onSubmit, onBack }) => {
  const [searchCompanyNameResults, setSearchCompanyNameResults] = useState([]);
  const lastAutocompleteSearchRef = useRef(null);
  const dispatch = useDispatch();

  const onAutocompleteProvider = async ({ value }) => {
    lastAutocompleteSearchRef.current = value;
    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_PROVIDER_GET_BY_AUTOCOMPLETE,
      queryId: value,
      payload: {
        searchInput: value,
        utilityTypeIds: [UTILITY_TYPE_ID_INTERNET],
        withRequiredTypes: true,
        limit: 4,
      },
    }).then((addresses) => {
      if (lastAutocompleteSearchRef.current === value) {
        setSearchCompanyNameResults(addresses);
      }
    });
  };

  return (
    <SrSimpleMessageModal
      iconUrl="/images/emoji-search.png"
      contentContainerClassName="utilityProviderContact"
      theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
      iconBgColor="#EBF8EF"
      maxIconHeight="100px"
      maxIconWidth="100px"
      headline={
        <SrBox maxWidth="300px" margin="0 auto">
          <SrText fontSize="24px" weight="800" font="greycliff">
            Type your current internet provider
          </SrText>
        </SrBox>
      }
      underline
      message={
        <SrBox>
          <Formik
            enableReinitialize
            render={() => (
              <Formik
                validate={createFormValidationFn(
                  getFormFields({
                    providerMatches: searchCompanyNameResults,
                    onAutocompleteProvider,
                  }),
                )}
                onSubmit={onSubmit}
                render={(formikBag) => {
                  const formFields = getFormFields({
                    providerMatches: searchCompanyNameResults,
                    onAutocompleteProvider,
                  });
                  return (
                    <Form autoComplete="off">
                      {formFields.map((formField) => (
                        <SrFormikField
                          key={formField.id}
                          formField={formField}
                          formikBag={formikBag}
                          className={SR_FIELD_THEME_ADOBE}
                        />
                      ))}
                      <SrBox marginTop="20px">
                        <SrButton type="submit" theme={SR_BUTTON_THEME_ADOBE_GREEN}>
                          Search
                        </SrButton>
                      </SrBox>
                      <SrBox marginTop="20px">
                        <SrButton type="submit" theme={SR_BUTTON_THEME_ADOBE_GREY} onClick={onBack}>
                          Back
                        </SrButton>
                      </SrBox>
                    </Form>
                  );
                }}
              />
            )}
          />
        </SrBox>
      }
    />
  );
};

SrUtilitySetupTransferInternetFlowCurrentProviderNameModal.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

SrUtilitySetupTransferInternetFlowCurrentProviderNameModal.displayName =
  'SrUtilitySetupTransferInternetFlowCurrentProviderNameModal'; // eslint-disable-line max-len

export default SrUtilitySetupTransferInternetFlowCurrentProviderNameModal;
